import api from '../../services/api';
import { ApiErrorResponse, CreateEstatePayload, CreateEstateResponse, EditEstateOwnerPayload, EditEstatePayload, EditEstateResponse, Estate, EstateRole, EstateRoleInvitePayload, EstateRoleInviteResponse } from '../../types';
import Cookies from 'js-cookie';

/**
 * Type guard to check if the error is an AxiosError.
 */
function isAxiosError(error: unknown): error is { response: { data: { message: string } } } {
  return typeof error === 'object' && error !== null && 'response' in error;
}

/**
 * Search estates based on a search query.
 * @param query - The search term.
 * @returns An array of estates matching the search criteria.
 */
export const searchEstates = async (query: string): Promise<Estate[]> => {
  try {
    const response = await api.get(`/estates/search?q=${encodeURIComponent(query)}`);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to search estates');
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Fetch all estates.
 * @returns An array of all estates.
 */
export const fetchEstates = async (): Promise<Estate[]> => {
  try {
    const response = await api.get('/estates');
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to fetch estates');
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Create a new estate.
 * @param estateData - The data for creating a new estate.
 * @param token - The authentication token.
 * @returns The response from the API.
 */
export const createEstate = async (
  estateData: CreateEstatePayload,
  token: string
): Promise<CreateEstateResponse> => {
  try {
    const response = await api.post('/estates', estateData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to create estate');
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Fetch details of a specific estate.
 * @param id - The ID of the estate.
 * @param token - The authentication token.
 * @returns The estate details.
 */
export const fetchEstateDetails = async (
  id: string,
  token: string
): Promise<Estate> => {
  try {
    const response = await api.get(`/estates/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to fetch estate details');
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Edit an existing estate.
 * @param uuid - The UUID of the estate to edit.
 * @param estateData - The updated estate data.
 * @param token - The authentication token.
 * @returns The response from the API.
 */
export const editEstate = async (
  uuid: string,
  estateData: EditEstatePayload,
  token: string
): Promise<EditEstateResponse> => {
  try {
    const response = await api.put(`/estates/${uuid}`, estateData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to edit estate');
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Edit the owner of an estate.
 *
 * Separate from editing the estate: the owner is a person, and correcting a
 * name should not mean restating the estate's own details.
 * @param uuid - The UUID of the estate whose owner is being edited.
 * @param ownerData - The updated owner data.
 * @returns The updated estate.
 */
export const editEstateOwner = async (
  uuid: string,
  ownerData: EditEstateOwnerPayload
): Promise<Estate> => {
  try {
    const response = await api.put(`/estates/${uuid}/owner`, ownerData);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to edit estate owner');
    }
    throw new Error('An unexpected error occurred');
  }
};



/**
 * Invite somebody to act for an estate, as its executor or as its lawyer.
 *
 * The role picks the endpoint; the body is the same either way, and the
 * acceptance link the API mails out is what lands them on the right roster.
 *
 * @param estateId - The ID of the estate.
 * @param role - Which roster the invite is for.
 * @param inviteData - The data for the invite.
 * @param token - The authentication token.
 * @returns The response from the API.
 */
export const inviteToEstateRole = async (
  estateId: string,
  role: EstateRole,
  inviteData: EstateRoleInvitePayload,
  token: string
): Promise<EstateRoleInviteResponse> => {
  try {
    const response = await api.post(`/estates/${estateId}/${role}s/invite`, inviteData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || `Failed to invite ${role}`);
    }
    throw new Error('An unexpected error occurred');
  }
};


/**
 * Delete an estate by estateId.
 * @param estateId - The ID of the estate to delete.
 * @param token - The authentication token.
 * @returns The response from the API.
 */
export const deleteEstate = async (estateId: string, token: string): Promise<void> => {
  try {
    await api.delete(`/estates/${estateId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to delete estate');
    }
    throw new Error('An unexpected error occurred');
  }
};


/**
 * Add Executor to Participants.
 * @param estateId - The ID of the estate to delete.
 * @param executorUid - The Executor UID.
 * @returns The response from the API.
 */
export const addExecutorToParticipants = async (estateId: string, executorUid: string): Promise<void> => {
  try {
    await api.get(`/estates/${estateId}/executors/${executorUid}/participant`);
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to add executor to participants');
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Take somebody off the executor or lawyer roster.
 *
 * Distinct from deleting a participant: it ends the role they act in, and
 * leaves them on the participants roster if they are also on it. Someone who
 * acts for the estate need not inherit from it, and the reverse holds too.
 *
 * @param estateId - The ID of the estate.
 * @param role - Which roster to take them off.
 * @param personUid - The UID of the person to remove.
 */
export const removeFromEstateRole = async (
  estateId: string,
  role: EstateRole,
  personUid: string
): Promise<void> => {
  try {
    await api.delete(`/estates/${estateId}/${role}s/${personUid}`);
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || `Failed to remove ${role}`);
    }
    throw new Error('An unexpected error occurred');
  }
}

/**
 * Add a lawyer to the participants roster as well.
 *
 * The two rosters answer different questions: the lawyer roster is who acts for
 * the estate, the participant roster is who its paperwork and notifications
 * reach. An attorney who is also a relative belongs on both.
 *
 * @param estateId - The ID of the estate.
 * @param lawyerUid - The Lawyer UID.
 */
export const addLawyerToParticipants = async (estateId: string, lawyerUid: string): Promise<void> => {
  try {
    await api.get(`/estates/${estateId}/lawyers/${lawyerUid}/participant`);
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response.data.message || 'Failed to add lawyer to participants');
    }
    throw new Error('An unexpected error occurred');
  }
}